// Support thread detail (PROP-163, design source PROP-153). The full
// inbound/outbound correspondence timeline plus a reply composer.
//
// Optimistic == post-refresh (CLAUDE.md, hard AC): on Send the reply
// endpoint returns the REHYDRATED thread (status flipped to "replied",
// the new outbound row already in the timeline) — we replace local state
// with that server shape and DO NOT refetch. A page refresh re-hydrates
// the identical timeline + status; no field appears only after a refetch
// and nothing is reconstructed from local input.
//
// Aligned with the reconcile inspector for chrome (PageHeader, back link,
// cardWrap, useToast); no parallel design system.

import { useEffect, useState, type ReactNode } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ApiError } from "@/lib/api";
import { PageHeader, ghostBtn, primaryBtn, Icon, cardWrap, Badge, BADGE_PALETTE, type BadgeTone } from "@/components/admin-ui";
import { useToast } from "@/components/ui-prims";
import {
  getSupportThread,
  replyToSupportThread,
  retrySupportReply,
  closeSupportThread,
  reopenSupportThread,
  type SupportThreadWithMessages,
  type SupportMessage,
  type SupportSendStatus,
} from "@/lib/support-api";
import { SupportStatusPill } from "./status-pill";

export function SupportThreadPage() {
  const { id = "" } = useParams<{ id: string }>();
  const toast = useToast();
  const navigate = useNavigate();

  const [thread, setThread] = useState<SupportThreadWithMessages | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error" | "notfound">("loading");

  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  // id of the outbound message currently being re-dispatched (one at a
  // time), so only its Retry button shows the in-flight state.
  const [retryingId, setRetryingId] = useState<string | null>(null);

  // Load the thread + its full timeline. The `alive` guard drops a stale
  // resolution if the operator navigated away before the fetch returned.
  useEffect(() => {
    let alive = true;
    setStatus("loading");
    setThread(null);
    getSupportThread(id)
      .then((resp) => {
        if (!alive) return;
        setThread(resp.thread);
        setStatus("ready");
      })
      .catch((err) => {
        if (!alive) return;
        if (err instanceof ApiError && err.status === 404) {
          setStatus("notfound");
          return;
        }
        console.error("getSupportThread", err);
        setStatus("error");
      });
    return () => {
      alive = false;
    };
  }, [id]);

  const onSend = async () => {
    const body = draft.trim();
    if (body === "" || sending) return;
    setSending(true);
    try {
      const resp = await replyToSupportThread(id, body);
      // Patch the whole view from the server response — the rehydrated
      // thread already carries the new outbound row in its timeline and
      // status="replied". No refetch; this is byte-identical to a refresh.
      setThread(resp.thread);
      setDraft("");
      toast("Reply sent · thread marked replied", { tone: "success" });
    } catch (err) {
      const e = err as { status?: number; message?: string };
      if (e?.status === 404) {
        toast("This thread no longer exists.", { tone: "error" });
      } else if (e?.status === 502) {
        toast("Reply saved but email dispatch failed — the thread was not marked replied.", { tone: "error" });
      } else if (e?.status === 503) {
        toast("Reply dispatch isn't configured on this environment.", { tone: "error" });
      } else {
        toast(e?.message || "Couldn't send the reply. Please try again.", { tone: "error" });
      }
    } finally {
      setSending(false);
    }
  };

  // Re-dispatch a failed outbound reply. On success the endpoint returns the
  // REHYDRATED thread (the re-sent row now send_status="sent", thread status
  // flipped back to "replied") — we replace local state with that server
  // shape and DO NOT refetch, so the optimistic view is byte-identical to a
  // page refresh. On failure we surface a toast and leave the row "failed"
  // (still retryable); engineering owns the status, never reconstructed here.
  const onRetry = async (msg: SupportMessage) => {
    if (retryingId) return;
    setRetryingId(msg.id);
    try {
      const resp = await retrySupportReply(id, msg.id);
      setThread(resp.thread);
      toast("Reply re-sent · thread marked replied", { tone: "success" });
    } catch (err) {
      const e = err as { status?: number; message?: string };
      if (e?.status === 404) {
        toast("This message no longer exists — refresh to see the latest.", { tone: "error" });
      } else if (e?.status === 409) {
        toast("This reply isn't in a failed state anymore — refresh to see the latest.", { tone: "error" });
      } else if (e?.status === 502) {
        // The server bumped send_attempts and returned the updated message on
        // the 502 body, but the shared ApiError only carries status+message
        // (not the JSON body), so we can't patch the inline attempt count here
        // without a refetch — it re-hydrates correctly on the next load. Known
        // small drift; tracked as a follow-up.
        toast("Re-send failed again — the reply is still marked failed and can be retried.", { tone: "error" });
      } else if (e?.status === 503) {
        toast("Reply dispatch isn't configured on this environment.", { tone: "error" });
      } else {
        toast(e?.message || "Couldn't re-send the reply. Please try again.", { tone: "error" });
      }
    } finally {
      setRetryingId(null);
    }
  };

  const onToggleStatus = async () => {
    if (!thread || statusBusy) return;
    const reopen = thread.status === "closed";
    setStatusBusy(true);
    try {
      const resp = reopen
        ? await reopenSupportThread(id)
        : await closeSupportThread(id);
      // The verb response carries the thread WITHOUT its message timeline;
      // merge the server-derived fields over local state and keep the
      // already-loaded messages (the mutation doesn't touch them).
      setThread((prev) => (prev ? { ...prev, ...resp.thread } : prev));
      toast(reopen ? "Thread reopened" : "Thread closed · audit logged", { tone: "success" });
    } catch (err) {
      const e = err as { status?: number; message?: string };
      toast(e?.message || "Couldn't update the thread status.", { tone: "error" });
    } finally {
      setStatusBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        kicker="Support"
        title={status === "ready" && thread ? (thread.subject || "(no subject)") : "Thread"}
        sub={
          status === "ready" && thread
            ? `${thread.from_name ? thread.from_name + " · " : ""}${thread.from_email}`
            : undefined
        }
        actions={
          status === "ready" && thread ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <SupportStatusPill status={thread.status} />
              <button
                type="button"
                style={ghostBtn}
                onClick={() => void onToggleStatus()}
                disabled={statusBusy}
              >
                {statusBusy ? "Working…" : thread.status === "closed" ? "Reopen" : "Close thread"}
              </button>
            </div>
          ) : undefined
        }
      />

      <div style={{ padding: "0 26px 32px" }}>
        <div style={{ marginBottom: 14 }}>
          <Link to="/support/inbox" style={{ ...ghostBtn, textDecoration: "none" }}>
            {Icon.arrowL} Back to inbox
          </Link>
        </div>

        {status === "loading" ? (
          <p style={{ fontSize: 13, color: "var(--ink-3)", fontStyle: "italic" }}>Loading thread…</p>
        ) : status === "notfound" ? (
          <p style={{ fontSize: 13, color: "var(--ink-3)" }}>
            This thread doesn't exist (it may have been removed).{" "}
            <button type="button" style={{ ...ghostBtn, marginLeft: 8 }} onClick={() => navigate("/support/inbox")}>
              Back to inbox
            </button>
          </p>
        ) : status === "error" ? (
          <p style={{ fontSize: 13, color: "var(--ink-3)" }}>
            Couldn't load this thread. Refresh the page to try again.
          </p>
        ) : thread ? (
          <>
            {/* Timeline */}
            <div style={{ ...cardWrap, margin: 0, padding: 0 }}>
              {thread.messages.length === 0 ? (
                <p style={{ fontSize: 13, color: "var(--ink-3)", fontStyle: "italic", padding: 16, margin: 0 }}>
                  No messages on this thread yet.
                </p>
              ) : (
                <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                  {thread.messages.map((m, i) => (
                    <MessageRow
                      key={m.id}
                      msg={m}
                      first={i === 0}
                      retrying={retryingId === m.id}
                      onRetry={() => void onRetry(m)}
                    />
                  ))}
                </ul>
              )}
            </div>

            {/* Reply composer */}
            <div style={{ marginTop: 18 }}>
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 600,
                  color: "var(--ink-4)",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  marginBottom: 8,
                }}
              >
                Reply to {thread.from_name || thread.from_email}
              </div>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={5}
                placeholder="Write a reply… The recipient, subject and threading headers are filled in server-side; you only write the message."
                disabled={sending}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid var(--rule)",
                  fontSize: 13,
                  fontFamily: "var(--font-ui)",
                  background: "var(--surface)",
                  color: "var(--ink)",
                  resize: "vertical",
                  minHeight: 110,
                  lineHeight: 1.55,
                }}
              />
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                <button
                  type="button"
                  style={{ ...primaryBtn, opacity: draft.trim() === "" || sending ? 0.55 : 1 }}
                  onClick={() => void onSend()}
                  disabled={draft.trim() === "" || sending}
                >
                  {Icon.send} {sending ? "Sending…" : "Send reply"}
                </button>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </>
  );
}

// MessageRow renders one inbound or outbound message. Outbound (admin
// reply) rows are tinted with the accent-soft surface and right-aligned
// meta so the timeline reads as a conversation. Outbound rows also carry a
// send-status badge (sent / pending / failed) derived from the server's
// send_status; inbound rows have an empty send_status and show no badge. A
// failed outbound row exposes its send_error plus a Retry button.
function MessageRow({
  msg,
  first,
  retrying,
  onRetry,
}: {
  msg: SupportMessage;
  first: boolean;
  retrying: boolean;
  onRetry: () => void;
}) {
  const outbound = msg.direction === "outbound";
  // send_status is "" for inbound rows (the column is NULL there) — only
  // outbound rows with a non-empty status get a badge.
  const sendStatus = msg.send_status;
  const failed = outbound && sendStatus === "failed";
  return (
    <li
      style={{
        padding: "14px 16px",
        borderTop: first ? "none" : "1px solid var(--rule)",
        background: outbound ? "var(--accent-soft)" : "transparent",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 6 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0, flexWrap: "wrap" }}>
          <span
            style={{
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              padding: "1px 6px",
              borderRadius: 4,
              background: outbound ? "var(--accent)" : "var(--surface-2)",
              color: outbound ? "#f4f1ea" : "var(--ink-3)",
            }}
          >
            {outbound ? "Reply" : "Inbound"}
          </span>
          <span style={{ fontSize: 12, fontWeight: 500, color: "var(--ink)", overflowWrap: "anywhere" }}>
            {msg.from_email || (outbound ? "Support" : "User")}
          </span>
          {outbound && sendStatus ? (
            <SendStatusBadge status={sendStatus} error={msg.send_error} />
          ) : null}
        </span>
        <span style={{ fontSize: 10.5, color: "var(--ink-4)", whiteSpace: "nowrap", flexShrink: 0 }}>
          {formatStamp(msg.received_at ?? msg.created_at)}
        </span>
      </div>
      <div
        style={{
          fontSize: 13,
          color: "var(--ink-2)",
          lineHeight: 1.6,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
        }}
      >
        {msg.body}
      </div>
      {failed && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginTop: 10,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: 11, color: BADGE_PALETTE.neg.fg, overflowWrap: "anywhere", minWidth: 0 }}>
            {msg.send_error ? `Dispatch failed: ${msg.send_error}` : "Dispatch failed."}
            {msg.send_attempts > 0
              ? ` · ${msg.send_attempts} attempt${msg.send_attempts === 1 ? "" : "s"}`
              : ""}
          </span>
          <button
            type="button"
            onClick={onRetry}
            disabled={retrying}
            style={{
              background: "transparent",
              border: `1px solid ${BADGE_PALETTE.neg.fg}`,
              color: BADGE_PALETTE.neg.fg,
              borderRadius: 6,
              padding: "4px 10px",
              fontSize: 11,
              fontWeight: 500,
              fontFamily: "var(--font-ui)",
              cursor: retrying ? "default" : "pointer",
              opacity: retrying ? 0.6 : 1,
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {retrying ? "Retrying…" : "Retry send"}
          </button>
        </div>
      )}
    </li>
  );
}

// SendStatusBadge renders an outbound reply's dispatch state via the shared
// admin-ui `Badge` (tones pos / mute / neg) — no parallel palette. `status`
// is the non-empty subset of SupportSendStatus — inbound rows ("" status)
// never reach here. On a failed badge the send_error rides in the Badge
// `title` so the operator sees the cause on hover (also shown inline below).
function SendStatusBadge({
  status,
  error,
}: {
  status: Exclude<SupportSendStatus, "">;
  error?: string;
}) {
  const meta = SEND_STATUS_META[status];
  return (
    <Badge tone={meta.tone} title={status === "failed" && error ? error : undefined}>
      {meta.icon}
      {meta.label}
    </Badge>
  );
}

const SEND_STATUS_META: Record<
  Exclude<SupportSendStatus, "">,
  { label: string; icon: ReactNode; tone: BadgeTone }
> = {
  // Sent — clean dispatch (positive tone, check glyph).
  sent: { label: "Sent", icon: Icon.check, tone: "pos" },
  // Pending — persisted but dispatch not yet confirmed (muted tone).
  pending: { label: "Pending", icon: null, tone: "mute" },
  // Failed — dispatch errored, retryable (negative tone, alert glyph).
  failed: { label: "Failed", icon: Icon.alert, tone: "neg" },
};

function formatStamp(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}
